import { ClaimStatusEnum, PermissionEnum, Title } from '@sr-sdks/api-sdk-axios';
import { Skeleton, cn } from '@sr/ui-library';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { invalidateTaskLists } from '../../api/task/taskApi';
import { useCheckUserPermissions } from '../../hooks/useCheckUserPermissions';
import { AppBadgeList } from '../Badges/AppBadgeList';
import { serviceBadgesFromTypes } from '../Badges/appBadgeConstants';
import { DraftDeleteAction } from '../DraftDeleteAction';
import { EntityCard } from '../EntityCard';
import { EntityCardViewAction } from '../EntityCardViewAction';
import { formatTitleMeta, isSeriesTitleType } from './titleMeta';

interface TitleCardProps {
  /** Workspace base path, e.g. `/workspace/<id>`. */
  basePath: string;
  /** Resolved owner-client display names (empty for non-agent workspaces). */
  clientNames: string[];
  /** True while the client names are still resolving (agent workspaces). */
  isClientNamesLoading?: boolean;
  title: Title;
}

/** 64 × 96 per the design, and the tallest thing in the card, so it sets the row height. */
const ICON_BOX_CLASS = 'h-24 w-16';

/**
 * Stand-in for a `TitleCard` while the list loads. Built from the same
 * `EntityCard` shell and the same icon box, so its height is *produced* by the
 * real markup rather than a hand-computed number that can silently fall out of
 * step — a placeholder of the wrong height shifts the pager under the cursor the
 * moment the data lands.
 */
export const TitleCardSkeleton = () => (
  <EntityCard data-testid="title-card-skeleton">
    <EntityCard.Content className="min-w-0">
      <Skeleton className={cn(ICON_BOX_CLASS, 'shrink-0 rounded-lg')} />

      <EntityCard.Info className="min-w-0">
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-3 w-64" />
        <Skeleton className="h-4 w-24" />
      </EntityCard.Info>
    </EntityCard.Content>
  </EntityCard>
);

/** A single row in the MSR3 Titles list — reuses the shared `EntityCard`. */
export const TitleCard = ({
  basePath,
  clientNames,
  isClientNamesLoading = false,
  title,
}: TitleCardProps) => {
  const queryClient = useQueryClient();
  const canDeleteDrafts = useCheckUserPermissions(
    [PermissionEnum.DeleteTitles],
    undefined,
    title.ownerWorkspaceId,
  );
  const summaryPath = `${basePath}/titles/summary/${title._id}`;
  // Only agent-held titles ever show a client name, so only they get a
  // placeholder while the names resolve — otherwise every row on the page grows a
  // grey bar that then vanishes with nothing taking its place.
  const isClientNamePending =
    isClientNamesLoading && Boolean(title.ownerClientIds?.length);

  return (
    <EntityCard>
      <EntityCard.Content className="min-w-0">
        {isSeriesTitleType(title.titleType) ? (
          <EntityCard.Icon
            classes={{ wrapper: 'h-24 w-16' }}
            icon="monitorPlay"
          />
        ) : (
          <EntityCard.Icon
            className="rotate-90"
            classes={{ wrapper: 'h-24 w-16' }}
            icon="filmStrip"
          />
        )}

        <EntityCard.Info className="min-w-0">
          <EntityCard.Title className="truncate">
            {/* `!`-important beats the global `.single-customer-app a` rule. */}
            <Link
              className="!text-foreground !no-underline hover:!underline"
              to={summaryPath}
            >
              {title.title}
            </Link>
          </EntityCard.Title>
          {/* Row wrapper, not a child of the `<p>`: the loading Skeleton is a
              `div` and cannot legally nest inside `EntityCard.Subtitle`. */}
          <div className="flex min-w-0 items-center gap-1">
            <EntityCard.Subtitle className="truncate">
              {formatTitleMeta(
                title.year,
                title.titleType,
                isClientNamePending ? undefined : clientNames.join(', '),
              )}
            </EntityCard.Subtitle>
            {/* Agent workspaces resolve client names separately from the title. */}
            {isClientNamePending && <Skeleton className="h-3 w-24" />}
          </div>
          <AppBadgeList types={serviceBadgesFromTypes(title.serviceType)} />
        </EntityCard.Info>
      </EntityCard.Content>

      <EntityCard.Actions>
        {title.claimStatus && (
          <EntityCard.Status className="hidden sm:flex">
            {title.claimStatus}
          </EntityCard.Status>
        )}
        {title.claimStatus === ClaimStatusEnum.Draft &&
          title._id &&
          title.ownerWorkspaceId &&
          canDeleteDrafts && (
            <DraftDeleteAction
              titleId={title._id}
              // The title list refetches by itself; the draft's task card
              // lives in the Tasks lists, which do not.
              onDeleteSuccess={() => invalidateTaskLists(queryClient)}
            />
          )}
        <EntityCardViewAction viewUrl={summaryPath} />
      </EntityCard.Actions>
    </EntityCard>
  );
};
